import { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import ComponentCard from "../../components/common/ComponentCard";
import PageMeta from "../../components/common/PageMeta";
import Button from "../../components/ui/button/Button";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Form from "../../components/form/Form";
import Select from "../../components/form/Select";
import MultiSelect from "../../components/form/MultiSelect";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import { CalenderIcon, PlusIcon, TrashBinIcon } from "../../icons";
import { jsPDF } from "jspdf";

const apiUrl = process.env.REACT_APP_API_URL;

type MultiSelectOption = {
  value: string;
  text: string;
  selected: boolean;
};

type MenuItemPhoto = {
  previewUrl: string;
  file: File | null;
};

type SpecialOccasionMenu = {
  id: number;
  eventName: string;
  startDate: string;
  endDate: string;
  venue: string[];
  venueOutlet: string[];
  mealType: string;
  items: {
    itemId?: string;
    name: string;
    photos: MenuItemPhoto[];
    dietaries: string[];
    membersPrice: string;
    nonMembersPrice: string;
  }[];
};

type SpecialMenuPermissions = {
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
};

const MAX_PHOTO_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);

const storageKey = "specialOccasionMenus";

const loadMenus = (): SpecialOccasionMenu[] => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((menu) => {
      if (!menu || typeof menu !== "object") return menu;
      if (Array.isArray(menu.items)) {
        const normalizedItems = menu.items.map((item: any) => ({
          name: typeof item?.name === "string" ? item.name : "",
          photos: Array.isArray(item?.photos)
            ? item.photos
                .map((photo: any) => {
                  if (typeof photo === "string") return { previewUrl: photo, file: null };
                  if (photo && typeof photo === "object" && typeof photo.previewUrl === "string") {
                    return { previewUrl: photo.previewUrl, file: null };
                  }
                  return null;
                })
                .filter(Boolean)
            : Array.isArray(item?.photoDataUrls)
              ? item.photoDataUrls
                  .map((photo: any) =>
                    typeof photo === "string" ? { previewUrl: photo, file: null } : null
                  )
                  .filter(Boolean)
              : typeof item?.photoDataUrl === "string" && item.photoDataUrl
                ? [{ previewUrl: item.photoDataUrl, file: null }]
                : [],
          dietaries: Array.isArray(item?.dietaries)
            ? item.dietaries
            : typeof item?.dietaries === "string" && item.dietaries
              ? [item.dietaries]
              : [],
          membersPrice: typeof item?.membersPrice === "string" ? item.membersPrice : "",
          nonMembersPrice: typeof item?.nonMembersPrice === "string" ? item.nonMembersPrice : "",
        }));
        return {
          ...menu,
          items: normalizedItems,
          venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
        };
      }
      if (typeof menu.items === "string") {
        const items = menu.items
          .split(",")
          .map((item: string) => item.trim())
          .filter(Boolean)
          .map((name: string) => ({
            name,
            photos: [],
            dietaries: [],
            membersPrice: "",
            nonMembersPrice: "",
          }));
        return {
          ...menu,
          items,
          venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
        };
      }
      return {
        ...menu,
        items: [],
        venueOutlet: Array.isArray(menu.venueOutlet) ? menu.venueOutlet : [],
      };
    });
  } catch {
    return [];
  }
};

const saveMenus = (menus: SpecialOccasionMenu[]) => {
  localStorage.setItem(storageKey, JSON.stringify(menus));
};

const emptyForm = {
  eventName: "",
  startDate: "",
  endDate: "",
  venue: [] as string[],
  venueOutlet: [] as string[],
  mealType: "",
  items: [
    {
      itemId: "",
      name: "",
      photos: [] as MenuItemPhoto[],
      dietaries: [] as string[],
      membersPrice: "",
      nonMembersPrice: "",
    },
  ],
};

const mealTypeOptions = [
  { value: "Breakfast", label: "Breakfast" },
  { value: "Lunch", label: "Lunch" },
  { value: "Dinner", label: "Dinner" },
  { value: "Special Event", label: "Special Event" },
];

export default function UpdateSpecialOccasionMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const menuToEdit = location.state?.menu;
  // console.log("Menu to edit:", menuToEdit); // Debugging log
  const token = localStorage.getItem("token");
  const [formData, setFormData] = useState(emptyForm);
  const [activePhoto, setActivePhoto] = useState<string | null>(null);
  const [venues, setVenues] = useState<any[]>([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [venueError, setVenueError] = useState("");
  const [venueOutletOptions, setVenueOutletOptions] = useState<MultiSelectOption[]>([]);
  const [venueOutletLoading, setVenueOutletLoading] = useState(false);
  const [venueOutletError, setVenueOutletError] = useState("");
  const [dietaryOptions, setDietaryOptions] = useState<MultiSelectOption[]>([]);
  const [dietaryLoading, setDietaryLoading] = useState(false);
  const [dietaryError, setDietaryError] = useState("");
  const [dietaryCodeMap, setDietaryCodeMap] = useState<Record<string, string>>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [permissions, setPermissions] = useState<SpecialMenuPermissions>({
    view: false,
    add: false,
    update: false,
    delete: false,
  });
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [hydratedMenu, setHydratedMenu] = useState<any | null>(null);
  const [pendingRemovedItemKeys, setPendingRemovedItemKeys] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<keyof typeof emptyForm, string>>({
    eventName: "",
    startDate: "",
    endDate: "",
    venue: "",
    venueOutlet: "",
    mealType: "",
    items: "",
  });

  useEffect(() => {
    const fetchAccess = async () => {
      if (!apiUrl) {
        setIsLoadingPermissions(false);
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/auth/access`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            token: token || "",
          },
        });

        if (response.status === 401) {
          localStorage.removeItem("token");
          navigate("/signin");
          return;
        }

        const result = await response.json();
        const access = result?.specialmenus;
        if (!access || typeof access !== "object") return;

        const nextPermissions: SpecialMenuPermissions = {
          view: Boolean(access.view),
          add: Boolean(access.add),
          update: Boolean(access.update),
          delete: Boolean(access.delete),
        };
        setPermissions(nextPermissions);
      } catch {
        // Keep page viewable; permissions default to hidden actions.
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchAccess();
  }, [navigate, token]);

  useEffect(() => {
    if (Array.isArray(formData.items)) return;
    setFormData((prev) => ({
      ...prev,
      items: [
        {
          itemId: "",
          name: "",
          photos: [],
          dietaries: [],
          membersPrice: "",
          nonMembersPrice: "",
        },
      ],
    }));
  }, [formData.items]);

  const fetchVenues = async () => {
    if (!apiUrl) {
      setVenueError("API URL is not configured.");
      return;
    }
    setVenueLoading(true);
    setVenueError("");
    const response = await fetch(`${apiUrl}/venues`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();
    // console.log(result);
    
    if (result.success) {
      setVenues(result.venues);
    } else {
      setVenues([]);
      setVenueError("Failed to load venues.");
    }

    if (!response.ok) {
      setVenueLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setVenueLoading(false);
  };

  useEffect(() => {
    fetchVenues();
  }, []);

  const fetchVenueOutlets = async () => {
    if (!apiUrl) {
      setVenueOutletError("API URL is not configured.");
      return;
    }

    setVenueOutletLoading(true);
    setVenueOutletError("");
    const response = await fetch(`${apiUrl}/special_occasion_menus/venue_outlets`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();

    if (result.success) {
      const options: MultiSelectOption[] = Array.isArray(result.venue_outlets)
        ? result.venue_outlets
            .map((venueOutlet: any) => ({
              value:
                typeof venueOutlet?.value === "string"
                  ? venueOutlet.value
                  : typeof venueOutlet?.venueoutlet_id === "number"
                    ? String(venueOutlet.venueoutlet_id)
                    : typeof venueOutlet?.venueoutlet_id === "string"
                      ? venueOutlet.venueoutlet_id
                      : typeof venueOutlet?.outlet_id === "number"
                        ? String(venueOutlet.outlet_id)
                        : typeof venueOutlet?.outlet_id === "string"
                          ? venueOutlet.outlet_id
                          : typeof venueOutlet?.id === "number"
                            ? String(venueOutlet.id)
                            : typeof venueOutlet?.id === "string"
                              ? venueOutlet.id
                              : typeof venueOutlet?.venue_id === "number"
                                ? String(venueOutlet.venue_id)
                                : typeof venueOutlet?.venue_id === "string"
                                  ? venueOutlet.venue_id
                      : typeof venueOutlet?.name === "string"
                        ? venueOutlet.name
                        : "",
              text:
                typeof venueOutlet?.label === "string"
                  ? venueOutlet.label
                  : typeof venueOutlet?.name === "string"
                    ? venueOutlet.name
                    : typeof venueOutlet?.venue_outlet_name === "string"
                      ? venueOutlet.venue_outlet_name
                    : "",
              selected: false,
            }))
            .filter((option: MultiSelectOption) => option.value && option.text)
        : [];
      setVenueOutletOptions(options);
    } else {
      setVenueOutletOptions([]);
      setVenueOutletError("Failed to load venue outlets.");
    }

    if (!response.ok) {
      setVenueOutletLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setVenueOutletLoading(false);
  };

  useEffect(() => {
    fetchVenueOutlets();
  }, []);

  const fetchDietaries = async () => {
    if (!apiUrl) {
      setDietaryError("API URL is not configured.");
      return;
    }
    setDietaryLoading(true);
    setDietaryError("");
    const response = await fetch(`${apiUrl}/special_occasion_menus/dietaries`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token: token || "",
      },
    });
    const result = await response.json();

    if (result.success) {
      const options: MultiSelectOption[] = Array.isArray(result.dietaries)
        ? result.dietaries
            .map((dietary: any) => ({
              value:
                typeof dietary?.dietary_id === "number"
                  ? String(dietary.dietary_id)
                  : typeof dietary?.dietary_id === "string"
                    ? dietary.dietary_id
                    : "",
              text: typeof dietary?.name === "string" ? dietary.name : "",
              selected: false,
            }))
            .filter((option: MultiSelectOption) => option.value && option.text)
        : [];
      setDietaryOptions(options);
      const codeMap: Record<string, string> = Array.isArray(result.dietaries)
        ? result.dietaries.reduce((acc: Record<string, string>, dietary: any) => {
            const code = typeof dietary?.code === "string" ? dietary.code : "";
            const id =
              typeof dietary?.dietary_id === "number"
                ? String(dietary.dietary_id)
                : typeof dietary?.dietary_id === "string"
                  ? dietary.dietary_id
                  : "";
            if (code && id) acc[code] = id;
            return acc;
          }, {})
        : {};
      setDietaryCodeMap(codeMap);
    } else {
      setDietaryOptions([]);
      setDietaryError("Failed to load dietaries.");
    }

    if (!response.ok) {
      setDietaryLoading(false);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    setDietaryLoading(false);
  };

  useEffect(() => {
    fetchDietaries();
  }, []);

  useEffect(() => {
    if (!dietaryCodeMap || Object.keys(dietaryCodeMap).length === 0) return;
    setFormData((prev) => {
      let changed = false;
      const nextItems = prev.items.map((item) => {
        if (!Array.isArray(item.dietaries)) return item;
        const mappedDietaries = item.dietaries.map((value) => {
          const asString = String(value);
          if (/^\d+$/.test(asString)) return asString;
          return dietaryCodeMap[asString] ?? asString;
        });
        const same =
          mappedDietaries.length === item.dietaries.length &&
          mappedDietaries.every((val, idx) => val === item.dietaries[idx]);
        if (same) return item;
        changed = true;
        return { ...item, dietaries: mappedDietaries };
      });
      return changed ? { ...prev, items: nextItems } : prev;
    });
  }, [dietaryCodeMap]);

  const fetchMenuById = async (menuId: string) => {
    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return null;
    }
    try {
      const response = await fetch(`${apiUrl}/special_occasion_menus/${menuId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });
      const result = await response.json();
      if (!response.ok || !result?.success) {
        throw new Error(result?.message || `Failed to load menu (${response.status})`);
      }
      return result?.specialOccasionMenu ?? result?.menu ?? result?.data ?? null;
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to load menu.");
      return null;
    }
  };

  const normalizeDateString = (value?: string) => {
    if (!value) return "";
    if (typeof value !== "string") return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return formatDateAsLocalYmd(parsed);
  };

  const normalizeStringArray = (value: any): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((entry) => {
        if (typeof entry === "string" || typeof entry === "number") {
          return String(entry);
        }
        if (entry && typeof entry === "object") {
          return String(
            entry.cst ??
              entry.id ??
              entry.venue_id ??
              entry.venueoutlet_id ??
              entry.outlet_id ??
              entry.venueId ??
              entry.value ??
              ""
          );
        }
        return "";
      })
      .filter(Boolean);
  };

  const getPhotoUrlFromValue = (photo: any): string => {
    if (typeof photo === "string") return photo;
    if (photo && typeof photo === "object") {
      if (typeof photo.previewUrl === "string") return photo.previewUrl;
      if (typeof photo.url === "string") return photo.url;
      if (typeof photo.photo_url === "string") return photo.photo_url;
      if (typeof photo.image_url === "string") return photo.image_url;
      if (typeof photo.image === "string") return photo.image;
      if (typeof photo.path === "string") return photo.path;
      if (typeof photo.src === "string") return photo.src;
    }
    return "";
  };

  const resolvePhotoUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
      return trimmed;
    }
    try {
      if (!apiUrl) return trimmed;
      return new URL(trimmed, apiUrl).toString();
    } catch {
      return trimmed;
    }
  };

  const normalizeItemPhotos = (item: any): MenuItemPhoto[] => {
    const rawPhotos: any[] = [];
    if (Array.isArray(item?.photos)) rawPhotos.push(...item.photos);
    if (Array.isArray(item?.photoDataUrls)) rawPhotos.push(...item.photoDataUrls);
    if (Array.isArray(item?.photo_urls)) rawPhotos.push(...item.photo_urls);
    if (Array.isArray(item?.images)) rawPhotos.push(...item.images);
    if (typeof item?.photoDataUrl === "string") rawPhotos.push(item.photoDataUrl);
    if (typeof item?.photo === "string") rawPhotos.push(item.photo);
    if (typeof item?.photo_url === "string") rawPhotos.push(item.photo_url);
    if (typeof item?.image === "string") rawPhotos.push(item.image);
    if (typeof item?.image_url === "string") rawPhotos.push(item.image_url);

    const seen = new Set<string>();
    const urls = rawPhotos
      .map(getPhotoUrlFromValue)
      .map(resolvePhotoUrl)
      .map((url) => url.trim())
      .filter((url) => {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      });

    return urls.map((url) => ({ previewUrl: url, file: null }));
  };

  useEffect(() => {
    const menu = menuToEdit ?? hydratedMenu;
    if (!menu) return;

    const rawItems = Array.isArray(menu.items)
      ? menu.items
      : typeof menu.items === "string"
        ? menu.items
            .split(",")
            .map((item: string) => item.trim())
            .filter(Boolean)
            .map((name: string) => ({
              name,
              photos: [],
              dietaries: [],
              membersPrice: "",
              nonMembersPrice: "",
            }))
        : [];

    const mappedItems = Array.isArray(rawItems)
      ? rawItems.map((item: any) => ({
          itemId:
            item?.item_id != null
              ? String(item.item_id)
              : item?.itemId != null
                ? String(item.itemId)
                : item?.id != null
                  ? String(item.id)
                  : "",
          name:
            typeof item?.name === "string"
              ? item.name
              : typeof item?.title === "string"
                ? item.title
                : "",
          photos: normalizeItemPhotos(item),
          dietaries: Array.isArray(item?.dietaries)
            ? item.dietaries.map((dietary: any) => {
                if (dietary && typeof dietary === "object") {
                  return String(
                    dietary.dietary_id ??
                      dietary.dietaryId ??
                      dietary.id ??
                      dietary.code ??
                      ""
                  );
                }
                return String(dietary ?? "");
              }).filter(Boolean)
            : typeof item?.dietaries === "string" && item.dietaries
              ? [item.dietaries]
              : [],
          membersPrice:
            item?.members_price != null
              ? String(item.members_price)
              : item?.membersPrice != null
                ? String(item.membersPrice)
                : "",
          nonMembersPrice:
            item?.non_members_price != null
              ? String(item.non_members_price)
              : item?.nonMembersPrice != null
                ? String(item.nonMembersPrice)
                : "",
        }))
      : [];

    const venueValues = normalizeStringArray(
      menu.venues ?? menu.venue ?? menu.venue_ids
    );
    const venueOutletValues = normalizeStringArray(
      menu.venue_outlets ?? menu.venueOutlet ?? menu.venue_outlet ?? menu.venue_outlet_ids
    );

    setFormData({
      eventName: menu.event_name ?? menu.eventName ?? menu.name ?? "",
      startDate: normalizeDateString(
        menu.start_date ?? menu.startDate ?? ""
      ),
      endDate: normalizeDateString(
        menu.end_date ?? menu.endDate ?? ""
      ),
      mealType: menu.meal_type ?? menu.mealType ?? "",
      venue: venueValues,
      venueOutlet: venueOutletValues,
      items: mappedItems.length > 0 ? mappedItems : emptyForm.items,
    });
    setPendingRemovedItemKeys([]);
  }, [menuToEdit, hydratedMenu]);

  const getItemKey = (item: { itemId?: string }, index: number) =>
    item.itemId ? `id:${item.itemId}` : `new:${index}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleMealTypeChange = (value: string) => {
    setFormData((prev) => ({ ...prev, mealType: value }));
    setErrors((prev) => ({ ...prev, mealType: "" }));
  };

  const handleVenueChange = (values: string[]) => {
    setFormData((prev) => ({ ...prev, venue: values }));
    setErrors((prev) => ({ ...prev, venue: "" }));
  };

  const handleVenueOutletChange = (values: string[]) => {
    setFormData((prev) => ({ ...prev, venueOutlet: values }));
    setErrors((prev) => ({ ...prev, venueOutlet: "" }));
  };

  const handleItemChange = (index: number, value: string) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], name: value };
      return { ...prev, items: nextItems };
    });
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const handleItemDietariesChange = (index: number, values: string[]) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], dietaries: values };
      return { ...prev, items: nextItems };
    });
  };

  const handleItemPriceChange = (
    index: number,
    field: "membersPrice" | "nonMembersPrice",
    value: string
  ) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      nextItems[index] = { ...nextItems[index], [field]: value };
      return { ...prev, items: nextItems };
    });
  };

  const handleItemPhotoChange = (index: number, files?: FileList | null) => {
    if (!files || files.length === 0) return;
    const selectedFiles = Array.from(files);
    const hasInvalidFile = selectedFiles.some(
      (file) => !ALLOWED_PHOTO_TYPES.has(file.type) || file.size > MAX_PHOTO_FILE_SIZE_BYTES
    );
    if (hasInvalidFile) {
      setSubmitError("Photos must be JPG, JPEG, PNG, or WEBP and no larger than 5MB each.");
      return;
    }

    setSubmitError("");
    setFormData((prev) => {
      const nextItems = [...prev.items];
      const existing = nextItems[index]?.photos ?? [];
      const uploadedPhotos: MenuItemPhoto[] = selectedFiles.map((file) => ({
        previewUrl: URL.createObjectURL(file),
        file,
      }));
      nextItems[index] = {
        ...nextItems[index],
        photos: [...existing, ...uploadedPhotos],
      };
      return { ...prev, items: nextItems };
    });
    setErrors((prev) => ({ ...prev, items: "" }));
  };

  const handleRemoveItemPhoto = (itemIndex: number, photoIndex: number) => {
    setFormData((prev) => {
      const nextItems = [...prev.items];
      const photoToRemove = nextItems[itemIndex].photos[photoIndex];
      if (photoToRemove?.file) {
        URL.revokeObjectURL(photoToRemove.previewUrl);
      }
      const nextPhotos = nextItems[itemIndex].photos.filter((_, idx) => idx !== photoIndex);
      nextItems[itemIndex] = { ...nextItems[itemIndex], photos: nextPhotos };
      return { ...prev, items: nextItems };
    });
  };

  const handleAddItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          itemId: "",
          name: "",
          photos: [],
          dietaries: [],
          membersPrice: "",
          nonMembersPrice: "",
        },
      ],
    }));
  };

  const handleRemoveItem = (index: number) => {
    const item = formData.items[index];
    if (!item) return;
    const itemKey = getItemKey(item, index);
    setPendingRemovedItemKeys((prev) =>
      prev.includes(itemKey)
        ? prev.filter((key) => key !== itemKey)
        : [...prev, itemKey]
    );
  };

  const closePhotoPreview = () => {
    setActivePhoto(null);
  };

  const getPhotoFileLabel = (photo: MenuItemPhoto) => {
    if (photo.file?.name) return photo.file.name;
    if (!photo.previewUrl) return "photo";
    const withoutQuery = photo.previewUrl.split("?")[0];
    const segments = withoutQuery.split("/");
    return decodeURIComponent(segments[segments.length - 1] || "photo");
  };

  const validate = () => {
    const nextErrors = { ...errors };

    if (!formData.eventName.trim()) nextErrors.eventName = "Event is required.";
    if (!formData.startDate) nextErrors.startDate = "Start date is required.";
    if (!formData.endDate) nextErrors.endDate = "End date is required.";
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      nextErrors.endDate = "End date must be on or after the start date.";
    }
    if (formData.venue.length === 0) nextErrors.venue = "Venue is required.";
    if (formData.venueOutlet.length === 0)
      nextErrors.venueOutlet = "Venue outlet is required.";
    if (!formData.mealType) nextErrors.mealType = "Meal type is required.";
    const validItems = formData.items.filter((item, index) => {
      const itemKey = getItemKey(item, index);
      if (pendingRemovedItemKeys.includes(itemKey)) return false;
      return item.name.trim().length > 0;
    });
    if (validItems.length === 0) nextErrors.items = "At least one item is required.";

    setErrors(nextErrors);

    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const activeMenu = menuToEdit ?? hydratedMenu;
    if (!activeMenu) {
      setSubmitError("No menu selected to update.");
      return;
    }
    if (!validate()) return;

    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError("");
    try {
      const resolvedMenuId =
        activeMenu?.menu_id ?? activeMenu?.menuId ?? activeMenu?.id ?? null;
      if (resolvedMenuId == null || resolvedMenuId === "") {
        throw new Error("Menu ID is missing. Please reopen this menu and try again.");
      }

      const validItems = formData.items
        .filter((item, index) => {
          const itemKey = getItemKey(item, index);
          return !pendingRemovedItemKeys.includes(itemKey);
        })
        .filter((item) => item.name.trim().length > 0);

      const itemsPayload = validItems.map((item) => {
        const parsedItemId = item.itemId ? Number(item.itemId) : NaN;
        const mappedItem: {
          item_id?: number;
          name: string;
          dietaries: number[];
          members_price: number;
          non_members_price: number;
        } = {
          name: item.name.trim(),
          dietaries: item.dietaries
            .map((dietaryId) => Number(dietaryId))
            .filter((dietaryId) => !Number.isNaN(dietaryId)),
          members_price: item.membersPrice ? Number(item.membersPrice) : 0,
          non_members_price: item.nonMembersPrice ? Number(item.nonMembersPrice) : 0,
        };

        if (!Number.isNaN(parsedItemId)) {
          mappedItem.item_id = parsedItemId;
        }

        return mappedItem;
      });

      const multipartBody = new FormData();
      multipartBody.append("menu_id", String(Number(resolvedMenuId)));
      multipartBody.append("event_name", formData.eventName.trim());
      multipartBody.append("start_date", formData.startDate);
      multipartBody.append("end_date", formData.endDate);
      multipartBody.append("meal_type", formData.mealType);
      multipartBody.append(
        "venues",
        JSON.stringify(
          formData.venue
            .map((venueId) => Number(venueId))
            .filter((venueId) => !Number.isNaN(venueId))
        )
      );
      multipartBody.append(
        "venue_outlets",
        JSON.stringify(
          formData.venueOutlet
            .map((venueOutletId) => Number(venueOutletId))
            .filter((venueOutletId) => !Number.isNaN(venueOutletId))
        )
      );
      multipartBody.append("items", JSON.stringify(itemsPayload));
      validItems.forEach((item, itemIndex) => {
        item.photos.forEach((photo) => {
          if (!photo.file) return;
          multipartBody.append(`item_${itemIndex}`, photo.file, photo.file.name);
          multipartBody.append("photo_item_keys[]", `item_${itemIndex}`);
          if (validItems.length === 1) {
            multipartBody.append("photos[]", photo.file, photo.file.name);
          }
        });
      });
      // console.log("Multipart Body:", multipartBody);

      const response = await fetch(`${apiUrl}/special_occasion_menus`, {
        method: "PUT",
        headers: {
          token: token || "",
        },
        body: multipartBody,
        
      });
      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || !result?.success) {
        throw new Error(
          result?.message ||
            responseText ||
            `Failed to save menu (${response.status})`
        );
      }

      navigate("/special-occasion-menus");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to save menu.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/special-occasion-menus");
  };

  const buildDisplayNames = (values: string[], options: MultiSelectOption[]) => {
    if (!Array.isArray(values) || values.length === 0) return "N/A";

    const optionMap = new Map(options.map((option) => [String(option.value), option.text]));
    const names = values
      .map((value) => optionMap.get(String(value)) || String(value))
      .map((value) => value.trim())
      .filter(Boolean);

    return names.length > 0 ? names.join(", ") : "N/A";
  };

  const buildDietaryNames = (dietaryIds: string[]) => {
    if (!Array.isArray(dietaryIds) || dietaryIds.length === 0) return "N/A";

    const dietaryMap = new Map(
      dietaryOptions.map((dietary) => [String(dietary.value), dietary.text])
    );
    const names = dietaryIds
      .map((id) => dietaryMap.get(String(id)) || String(id))
      .map((value) => value.trim())
      .filter(Boolean);

    return names.length > 0 ? names.join(", ") : "N/A";
  };

  const formatPriceForPdf = (rawPrice: string) => {
    const parsed = Number(rawPrice);
    if (Number.isNaN(parsed)) return rawPrice || "0.00";
    return parsed.toFixed(2);
  };

  const getImageDataUrlForPdf = async (sourceUrl: string): Promise<string | null> => {
    if (!sourceUrl) return null;
    if (sourceUrl.startsWith("data:")) return sourceUrl;
    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(typeof reader.result === "string" ? reader.result : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const detectPdfImageType = (dataUrl: string): "JPEG" | "PNG" | "WEBP" => {
    if (dataUrl.startsWith("data:image/png")) return "PNG";
    if (dataUrl.startsWith("data:image/webp")) return "WEBP";
    return "JPEG";
  };

  const getHeaderLogoDataUrl = async (): Promise<string | null> => {
    if (!apiUrl || !token) return null;
    try {
      const response = await fetch(`${apiUrl}/syscore/logo`, {
        method: "GET",
        headers: {
          token,
        },
      });
      if (!response.ok) return null;
      const result = await response.json();
      const logoUrl =
        typeof result?.logoUrl === "string"
          ? result.logoUrl
          : typeof result?.logo_url === "string"
            ? result.logo_url
            : "";
      if (!logoUrl) return null;
      const absoluteUrl =
        /^https?:\/\//i.test(logoUrl) || logoUrl.startsWith("data:")
          ? logoUrl
          : `${apiUrl.replace(/\/$/, "")}/${logoUrl.replace(/^\//, "")}`;
      return await getImageDataUrlForPdf(absoluteUrl);
    } catch {
      return null;
    }
  };

  const handleDownloadPdf = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 36;
    const pageTop = 36;
    const pageBottom = 42;
    const contentWidth = pageWidth - marginX * 2;
    const headerHeight = 104;
    const headerLogoDataUrl = await getHeaderLogoDataUrl();
    let cursorY = pageTop + headerHeight + 18;

    const colors = {
      primary: [21, 63, 107] as [number, number, number],
      primarySoft: [234, 242, 252] as [number, number, number],
      cardBg: [247, 249, 252] as [number, number, number],
      cardBorder: [218, 225, 234] as [number, number, number],
      textDark: [31, 41, 55] as [number, number, number],
      textMuted: [90, 107, 126] as [number, number, number],
      white: [255, 255, 255] as [number, number, number],
    };

    const drawHeader = () => {
      doc.setFillColor(...colors.primary);
      doc.roundedRect(marginX, pageTop, contentWidth, headerHeight, 10, 10, "F");

      doc.setTextColor(...colors.white);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.text("Special Occasion Menu", marginX + 18, pageTop + 34);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Event: ${formData.eventName || "N/A"}`, marginX + 18, pageTop + 56);

      doc.text("Menu Export", marginX + 18, pageTop + 76);

      if (headerLogoDataUrl) {
        try {
          const imageProps = doc.getImageProperties(headerLogoDataUrl);
          const rawWidth = Number(imageProps?.width) || 1;
          const rawHeight = Number(imageProps?.height) || 1;
          const maxWidth = 110;
          const maxHeight = 44;
          const scale = Math.min(maxWidth / rawWidth, maxHeight / rawHeight);
          const logoWidth = Math.max(1, rawWidth * scale);
          const logoHeight = Math.max(1, rawHeight * scale);
          const logoX = pageWidth - marginX - 18 - logoWidth;
          const logoY = pageTop + 12;
          const logoPaddingX = 10;
          const logoPaddingY = 6;

          // Improve contrast for dark/blue logos against the dark header.
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(225, 232, 242);
          doc.roundedRect(
            logoX - logoPaddingX,
            logoY - logoPaddingY,
            logoWidth + logoPaddingX * 2,
            logoHeight + logoPaddingY * 2,
            6,
            6,
            "FD"
          );

          doc.addImage(
            headerLogoDataUrl,
            detectPdfImageType(headerLogoDataUrl),
            logoX,
            logoY,
            logoWidth,
            logoHeight,
            undefined,
            "SLOW"
          );
        } catch {
          // If logo cannot be embedded, keep the existing header without a fallback logo.
        }
      }

      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.6);
      doc.line(marginX + 18, pageTop + 87, pageWidth - marginX - 18, pageTop + 87);
      doc.setFontSize(10);
    };

    const ensureSpace = (requiredHeight: number) => {
      if (cursorY + requiredHeight <= pageHeight - pageBottom) return;
      doc.addPage();
      drawHeader();
      cursorY = pageTop + headerHeight + 18;
    };

    const drawSectionTitle = (title: string) => {
      ensureSpace(28);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...colors.primary);
      doc.text(title, marginX, cursorY);
      cursorY += 16;
    };

    const drawInfoRow = (label: string, value: string) => {
      const rowX = marginX;
      const rowY = cursorY;
      const rowWidth = contentWidth;
      const labelWidth = 118;
      const textPadding = 12;
      const valueLines = doc.splitTextToSize(value || "N/A", rowWidth - labelWidth - 24) as string[];
      const rowHeight = Math.max(30, valueLines.length * 13 + textPadding * 2 - 8);

      ensureSpace(rowHeight + 8);

      doc.setFillColor(...colors.cardBg);
      doc.setDrawColor(...colors.cardBorder);
      doc.roundedRect(rowX, rowY, rowWidth, rowHeight, 6, 6, "FD");

      doc.setFillColor(...colors.primarySoft);
      doc.roundedRect(rowX, rowY, labelWidth, rowHeight, 6, 6, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...colors.primary);
      doc.text(label, rowX + textPadding, rowY + 19);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...colors.textDark);
      let lineY = rowY + 19;
      valueLines.forEach((line) => {
        doc.text(line, rowX + labelWidth + 10, lineY);
        lineY += 13;
      });

      cursorY += rowHeight + 8;
    };

    const drawItemCard = async (
      item: {
        name: string;
        dietaries: string[];
        membersPrice: string;
        nonMembersPrice: string;
        photos: MenuItemPhoto[];
      }
    ) => {
      const cardX = marginX;
      const cardWidth = contentWidth;
      const innerX = cardX + 12;
      const innerWidth = cardWidth - 24;
      const maxThumbsPerRow = 2;
      const thumbGap = 8;

      const drawItemContainer = (height: number) => {
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(...colors.cardBorder);
        doc.roundedRect(cardX, cursorY, cardWidth, height, 8, 8, "FD");
      };

      const ensureItemChunkSpace = (requiredHeight: number) => {
        ensureSpace(requiredHeight + 10);
      };

      const titleLines = doc.splitTextToSize(item.name || "Unnamed Item", innerWidth) as string[];
      const dietaryText = `Dietaries: ${buildDietaryNames(item.dietaries)}`;
      const dietaryLines = doc.splitTextToSize(dietaryText, innerWidth) as string[];
      const photos = Array.isArray(item.photos)
        ? item.photos.filter((photo) => typeof photo?.previewUrl === "string" && photo.previewUrl.trim())
        : [];
      const gridWidth = innerWidth;
      const rowLayouts: { startIndex: number; count: number; width: number; height: number }[] = [];
      for (let startIndex = 0; startIndex < photos.length; startIndex += maxThumbsPerRow) {
        const count = Math.min(maxThumbsPerRow, photos.length - startIndex);
        const width = count === 1 ? gridWidth : (gridWidth - thumbGap * (count - 1)) / count;
        const height = Math.round(width * 0.72);
        rowLayouts.push({ startIndex, count, width, height });
      }

      const introHeight = 20 + titleLines.length * 13 + dietaryLines.length * 12 + 24 + (photos.length > 0 ? 18 : 0);
      ensureItemChunkSpace(introHeight);
      const introY = cursorY;
      drawItemContainer(introHeight);
      doc.setTextColor(...colors.textDark);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      let textY = introY + 18;
      titleLines.forEach((line) => {
        doc.text(line, innerX, textY);
        textY += 13;
      });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...colors.textMuted);
      dietaryLines.forEach((line) => {
        doc.text(line, innerX, textY);
        textY += 12;
      });

      const priceDividerY = introY + introHeight - (photos.length > 0 ? 24 : 14);
      doc.setDrawColor(...colors.cardBorder);
      doc.line(cardX + 10, priceDividerY, cardX + cardWidth - 10, priceDividerY);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...colors.textDark);
      doc.text(`Members: $${formatPriceForPdf(item.membersPrice)}`, innerX, priceDividerY + 14);
      doc.text(
        `Non-Members: $${formatPriceForPdf(item.nonMembersPrice)}`,
        cardX + cardWidth / 2 + 8,
        priceDividerY + 14
      );
      cursorY += introHeight + 6;
      if (photos.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(...colors.primary);
        doc.text("Photos", innerX, cursorY + 8);
        cursorY += 14;
        for (const row of rowLayouts) {
          ensureItemChunkSpace(row.height + 12);
          const rowY = cursorY;
          drawItemContainer(row.height + 10);
          for (let col = 0; col < row.count; col += 1) {
            const photoIndex = row.startIndex + col;
            const x = innerX + col * (row.width + thumbGap);
            const y = rowY + 5;

            doc.setDrawColor(...colors.cardBorder);
            doc.roundedRect(x, y, row.width, row.height, 3, 3, "S");

            const dataUrl = await getImageDataUrlForPdf(photos[photoIndex].previewUrl);
            if (!dataUrl) continue;

            try {
              const imageProps = doc.getImageProperties(dataUrl);
              const rawWidth = Number(imageProps?.width) || row.width;
              const rawHeight = Number(imageProps?.height) || row.height;
              const scale = Math.min(row.width / rawWidth, row.height / rawHeight);
              const drawWidth = Math.max(1, rawWidth * scale);
              const drawHeight = Math.max(1, rawHeight * scale);
              const offsetX = x + (row.width - drawWidth) / 2;
              const offsetY = y + (row.height - drawHeight) / 2;

              doc.setFillColor(255, 255, 255);
              doc.rect(x, y, row.width, row.height, "F");
              doc.addImage(
                dataUrl,
                detectPdfImageType(dataUrl),
                offsetX,
                offsetY,
                drawWidth,
                drawHeight,
                undefined,
                "SLOW"
              );
            } catch {
              // Skip invalid or unsupported images without failing the full export.
            }
          }
          cursorY += row.height + 12;
        }
      }

      cursorY += 6;
    };

    const venueOptions: MultiSelectOption[] = venues
      .map((venue) => ({
        value:
          typeof venue?.cst === "string"
            ? venue.cst
            : typeof venue?.cst === "number"
              ? String(venue.cst)
              : "",
        text: typeof venue?.name === "string" ? venue.name : "",
        selected: false,
      }))
      .filter((option) => option.value && option.text);

    const printableItems = formData.items.filter((item, index) => {
      const itemKey = getItemKey(item, index);
      return !pendingRemovedItemKeys.includes(itemKey) && item.name.trim().length > 0;
    });

    const safeEventName = formData.eventName.trim() || "special-occasion-menu";
    const fileName = `${safeEventName.replace(/[^a-z0-9-_]+/gi, "-").toLowerCase()}.pdf`;

    drawHeader();

    drawSectionTitle("Menu Details");
    drawInfoRow("Meal Type", formData.mealType || "N/A");
    drawInfoRow("Start Date", formData.startDate || "N/A");
    drawInfoRow("End Date", formData.endDate || "N/A");
    drawInfoRow("Venue", buildDisplayNames(formData.venue, venueOptions));
    drawInfoRow(
      "Venue Outlets",
      buildDisplayNames(formData.venueOutlet, venueOutletOptions)
    );

    cursorY += 12;
    drawSectionTitle("Menu Items");

    if (printableItems.length === 0) {
      ensureSpace(44);
      doc.setFillColor(...colors.cardBg);
      doc.setDrawColor(...colors.cardBorder);
      doc.roundedRect(marginX, cursorY, contentWidth, 38, 6, 6, "FD");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...colors.textMuted);
      doc.text("No menu items available.", marginX + 12, cursorY + 24);
      cursorY += 46;
    } else {
      for (const item of printableItems) {
        await drawItemCard(item);
      }
    }

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(...colors.cardBorder);
      doc.setLineWidth(0.4);
      doc.line(marginX, pageHeight - 24, pageWidth - marginX, pageHeight - 24);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...colors.textMuted);
      doc.text(
        `${formData.eventName || "Special Occasion Menu"} - Page ${page} of ${pageCount}`,
        marginX,
        pageHeight - 12
      );
    }

    doc.save(fileName);
  };

  const handleDeleteMenu = async () => {
    const activeMenu = menuToEdit ?? hydratedMenu;
    if (!activeMenu) {
      setSubmitError("No menu selected to delete.");
      return;
    }
    const resolvedMenuId =
      activeMenu?.menu_id ?? activeMenu?.menuId ?? activeMenu?.id ?? null;
    if (resolvedMenuId == null || resolvedMenuId === "") {
      setSubmitError("Menu ID is missing. Please reopen this menu and try again.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this menu?")) return;

    if (!apiUrl) {
      setSubmitError("API URL is not configured.");
      return;
    }

    setDeleteLoading(true);
    setSubmitError("");
    try {
      const response = await fetch(`${apiUrl}/special_occasion_menus/${resolvedMenuId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          token: token || "",
        },
      });
      const responseText = await response.text();
      let result: any = null;
      try {
        result = responseText ? JSON.parse(responseText) : null;
      } catch {
        result = null;
      }

      if (!response.ok || (result && result.success === false)) {
        throw new Error(
          result?.message ||
            responseText ||
            `Failed to delete menu (${response.status})`
        );
      }

      navigate("/special-occasion-menus");
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to delete menu.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDateAsLocalYmd = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (field: "startDate" | "endDate") => (date: Date[]) => {
    if (date.length > 0) {
      const formattedDate = formatDateAsLocalYmd(date[0]);
      setFormData((prev) => ({
        ...prev,
        [field]: formattedDate,
      }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  if (isLoadingPermissions) {
    return null;
  }

  return (
    <>
      <PageMeta
        title="Update Special Occasion Menu | Hospitality Genie"
        description="Create a special menu for a specific event or occasion."
      />
      
        <ComponentCard title="Update Special Occasion Menu">
          <Form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <div className="col-span-full">
                <h4 className="pb-4 text-base font-medium text-gray-800 border-b border-gray-200 dark:border-gray-800 dark:text-white/90">
                  Menu Details
                </h4>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>Event</Label>
                    <Input
                      type="text"
                      name="eventName"
                      value={formData.eventName}
                      onChange={handleChange}
                    />
                    {errors.eventName && <p className="text-red-500 text-sm">{errors.eventName}</p>}
                  </div>
                  <div>
                    <Label>Meal Type</Label>
                    <Select
                      options={mealTypeOptions}
                      value={formData.mealType}
                      onChange={handleMealTypeChange}
                      className="mt-2 bg-gray-50 dark:bg-gray-800"
                    />
                    {errors.mealType && <p className="text-red-500 text-sm">{errors.mealType}</p>}
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <div className="relative flatpickr-wrapper w-full">
                      <Flatpickr
                        value={formData.startDate}
                        onChange={handleDateChange("startDate")}
                        options={{
                          dateFormat: "Y-m-d",
                        }}
                        className="w-full py-2 pl-3 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                      <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
                        <CalenderIcon />
                      </span>
                    </div>
                    {errors.startDate && <p className="text-red-500 text-sm">{errors.startDate}</p>}
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <div className="relative flatpickr-wrapper w-full">
                      <Flatpickr
                        value={formData.endDate}
                        onChange={handleDateChange("endDate")}
                        options={{
                          dateFormat: "Y-m-d",
                          minDate: formData.startDate || undefined,
                        }}
                        className="w-full py-2 pl-3 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                      />
                      <span className="absolute text-gray-500 -translate-y-1/2 pointer-events-none right-3 top-1/2 dark:text-gray-400">
                        <CalenderIcon />
                      </span>
                    </div>
                    {errors.endDate && <p className="text-red-500 text-sm">{errors.endDate}</p>}
                  </div>
                  <div>
                    <Label>Venue</Label>
                    <MultiSelect
                      label=""
                      options={venues
                        .map((venue) => ({
                          value:
                            typeof venue?.cst === "string"
                              ? venue.cst
                              : typeof venue?.cst === "number"
                                ? String(venue.cst)
                                : "",
                          text: typeof venue?.name === "string" ? venue.name : "",
                          selected: false,
                        }))
                        .filter((option) => option.value && option.text)}
                      defaultSelected={formData.venue}
                      value={formData.venue}
                      onChange={handleVenueChange}
                      closeOnSelect
                      disabled={venueLoading || venues.length === 0}
                    />
                    {venueLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading venues...</p>
                    )}
                    {venueError && <p className="text-red-500 text-sm">{venueError}</p>}
                    {errors.venue && <p className="text-red-500 text-sm">{errors.venue}</p>}
                  </div>
                  <div>
                    <Label>Venue Outlets</Label>
                    <MultiSelect
                      label=""
                      options={venueOutletOptions}
                      defaultSelected={formData.venueOutlet}
                      value={formData.venueOutlet}
                      onChange={handleVenueOutletChange}
                      closeOnSelect
                      disabled={venueOutletLoading || venueOutletOptions.length === 0}
                    />
                    {venueOutletLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading venue outlets...</p>
                    )}
                    {venueOutletError && (
                      <p className="text-red-500 text-sm">{venueOutletError}</p>
                    )}
                    {errors.venueOutlet && (
                      <p className="text-red-500 text-sm">{errors.venueOutlet}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center justify-between">
                  <Label>Items</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    startIcon={<PlusIcon className="h-4 w-4" />}
                    onClick={handleAddItem}
                  >
                    Add Item
                  </Button>
                </div>
                <div className="mt-3 space-y-4">
                  {formData.items.map((item, index) => (
                    (() => {
                      const itemKey = getItemKey(item, index);
                      const isPendingRemove = pendingRemovedItemKeys.includes(itemKey);
                      return (
                    <div
                      key={`item-${index}`}
                      className={`rounded-xl border p-4 dark:border-gray-800 ${
                        isPendingRemove
                          ? "border-red-200 bg-red-50/50 dark:border-red-900/40 dark:bg-red-900/10"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-white/80">
                          Item {index + 1}
                          {isPendingRemove && (
                            <span className="ml-2 text-xs font-normal text-red-600 dark:text-red-300">
                              (Will be removed on update)
                            </span>
                          )}
                        </p>
                        {formData.items.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            startIcon={<TrashBinIcon className="h-4 w-4" />}
                            className="!bg-red-500 !text-white !ring-red-500 hover:!bg-red-600 dark:!bg-red-500 dark:hover:!bg-red-600 dark:!ring-red-500"
                            onClick={() => handleRemoveItem(index)}
                          >
                            {isPendingRemove ? "Undo" : "Remove"}
                          </Button>
                        )}
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <Label>Item Name</Label>
                          <textarea
                            name={`item-${index}-name`}
                            value={item.name}
                            onChange={(e) => handleItemChange(index, e.target.value)}
                            rows={2}
                            disabled={isPendingRemove}
                            className="w-full rounded-lg border px-4 py-2.5 text-sm shadow-theme-xs focus:outline-none text-gray-600 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
                          />
                        </div>
                        <div>
                          <Label>Dietaries</Label>
                          <MultiSelect
                            label=""
                            options={dietaryOptions}
                            defaultSelected={item.dietaries}
                            value={item.dietaries}
                            onChange={(values) => handleItemDietariesChange(index, values)}
                            closeOnSelect
                            disabled={isPendingRemove || dietaryLoading || dietaryOptions.length === 0}
                          />
                          {dietaryLoading && (
                            <p className="text-xs text-gray-500 mt-2">Loading dietaries...</p>
                          )}
                          {dietaryError && (
                            <p className="text-red-500 text-sm">{dietaryError}</p>
                          )}
                        </div>
                        <div>
                          <Label>Members Price $</Label>
                          <Input
                            type="number"
                            name={`item-${index}-members-price`}
                            value={item.membersPrice}
                            onChange={(e) =>
                              handleItemPriceChange(index, "membersPrice", e.target.value)
                            }
                            disabled={isPendingRemove}
                          />
                        </div>
                        <div>
                          <Label>Non-Members Price $</Label>
                          <Input
                            type="number"
                            name={`item-${index}-non-members-price`}
                            value={item.nonMembersPrice}
                            onChange={(e) =>
                              handleItemPriceChange(index, "nonMembersPrice", e.target.value)
                            }
                            disabled={isPendingRemove}
                          />
                        </div>
                      </div>

                      <div className="mt-3">
                        <Label>Photos</Label>
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={(e) => handleItemPhotoChange(index, e.target.files)}
                          disabled={isPendingRemove}
                          className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-lg file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-gray-700 hover:file:bg-gray-200 dark:text-gray-300 dark:file:bg-gray-800 dark:file:text-gray-200"
                        />
                      </div>

                      {item.photos.length > 0 && (
                        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                          {item.photos.map((photo, photoIndex) => (
                            <div key={`${index}-photo-${photoIndex}`} className="relative">
                              <img
                                src={photo.previewUrl}
                                alt={item.name || `Item ${index + 1}`}
                                className="h-24 w-24 rounded-lg object-cover"
                                role="button"
                                tabIndex={0}
                                onClick={() => setActivePhoto(photo.previewUrl)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    setActivePhoto(photo.previewUrl);
                                  }
                                }}
                              />
                              <p className="mt-1 w-24 truncate text-xs text-gray-500 dark:text-gray-400">
                                {getPhotoFileLabel(photo)}
                              </p>
                              {/* <button
                                type="button"
                                onClick={() => handleRemoveItemPhoto(index, photoIndex)}
                                aria-label="Remove photo"
                                disabled={isPendingRemove}
                                className="absolute -right-2 -top-2 inline-flex items-center justify-center rounded-full bg-red-500 p-1.5 text-white hover:bg-red-600"
                              >
                                <TrashBinIcon className="h-3.5 w-3.5" />
                              </button> */}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                      );
                    })()
                  ))}
                </div>
                {errors.items && <p className="mt-3 text-red-500 text-sm">{errors.items}</p>}
              </div>
              <div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-800">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadPdf}
                  className="min-w-[108px] rounded-xl text-base font-semibold"
                >
                  Download PDF
                </Button>
                {permissions.update && (
                  <Button
                    type="submit"
                    size="sm"
                    disabled={submitLoading}
                    className="min-w-[108px] rounded-xl text-base font-semibold"
                  >
                    {submitLoading ? "Saving..." : "Update"}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  className="min-w-[108px] rounded-xl text-base font-semibold"
                >
                  Cancel
                </Button>
                {permissions.delete && (
                  <button
                    type="button"
                    onClick={handleDeleteMenu}
                    disabled={deleteLoading || submitLoading}
                    className="inline-flex min-w-[108px] items-center justify-center rounded-xl bg-red-700 px-6 py-3 text-base font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleteLoading ? "Deleting..." : "Delete"}
                  </button>
                )}
                {submitError && (
                  <p className="w-full text-sm text-red-500">{submitError}</p>
                )}
              </div>
            </div>
          </Form>
        </ComponentCard>
      {activePhoto && (
        <div
          className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/70 p-6"
          onClick={closePhotoPreview}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="max-h-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={activePhoto}
              alt="Selected menu item"
              className="max-h-[80vh] w-auto rounded-xl object-contain shadow-lg"
            />
          </div>
        </div>
      )}
    </>
  );
}
